/**
 * VesselStatusCard - Shows vessel status on Dashboard
 * Re-exports the TypeScript component for use in JS files
 */

import React, { useMemo } from 'react';
import {
  Ship,
  Anchor,
  Fuel,
  Users,
  Navigation,
  Clock,
  MapPin,
  ChevronRight
} from 'lucide-react';
import { useVoyages } from '../../hooks/domain/operations/useVoyages';
import { portLabel } from '../../utils/voyage/portLabel';

// Status configuration
const STATUS_CONFIG = {
  underway: {
    label: 'Underway',
    icon: Ship,
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    borderColor: 'border-green-200',
    dotColor: 'bg-green-500',
    animate: true
  },
  pre_departure: {
    label: 'Pre-Departure',
    icon: Navigation,
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
    dotColor: 'bg-amber-500',
    animate: false
  },
  arrival: {
    label: 'Arrived',
    icon: MapPin,
    bgColor: 'bg-cyan-100',
    textColor: 'text-cyan-700',
    borderColor: 'border-cyan-200',
    dotColor: 'bg-cyan-500',
    animate: false
  },
  alongside: {
    label: 'Alongside',
    icon: Anchor,
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
    dotColor: 'bg-blue-500',
    animate: false
  }
};

// Fuel level color coding (matches existing FuelGaugeBar.js)
const getFuelColor = (level) => {
  if (level >= 75) return { bg: 'bg-green-500', text: 'text-green-600' };
  if (level >= 50) return { bg: 'bg-cyan-500', text: 'text-cyan-600' };
  if (level >= 25) return { bg: 'bg-amber-500', text: 'text-amber-600' };
  return { bg: 'bg-red-500', text: 'text-red-600' };
};

// Format duration helper
const formatDuration = (ms) => {
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

// Format time helper (time only)
const formatTime = (date) => {
  return new Date(date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

// Format date+time helper (for DEP/ETA display)
const formatDateTime = (date) => {
  const d = new Date(date);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();

  if (isToday) {
    // Just show time if same day
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  // Show date + time if different day
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' +
         d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

// Format distance helper
const formatDistanceToNow = (date) => {
  const now = new Date();
  const diff = now - new Date(date);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  return 'recently';
};

export const VesselStatusCard = ({ onNavigate }) => {
  const { activeVoyage, voyages } = useVoyages();

  // Determine vessel status
  const vesselStatus = useMemo(() => {
    if (!activeVoyage) return 'alongside';
    return activeVoyage.status || 'alongside';
  }, [activeVoyage]);

  const statusConfig = STATUS_CONFIG[vesselStatus] || STATUS_CONFIG.alongside;
  const StatusIcon = statusConfig.icon;

  // Calculate POB
  const totalPOB = useMemo(() => {
    if (activeVoyage) {
      return (activeVoyage.crewCount || 0) + (activeVoyage.passengerCount || 0);
    }
    return 0;
  }, [activeVoyage]);

  // Calculate voyage duration
  const voyageDuration = useMemo(() => {
    if (!activeVoyage?.departureTime) return null;
    try {
      const duration = Date.now() - new Date(activeVoyage.departureTime).getTime();
      return formatDuration(duration);
    } catch {
      return null;
    }
  }, [activeVoyage?.departureTime]);

  // Get fuel level
  const fuelLevel = activeVoyage?.currentFuel ?? null;
  const fuelColor = fuelLevel !== null ? getFuelColor(fuelLevel) : null;

  // Get last completed voyage (for "alongside" state)
  const lastVoyage = useMemo(() => {
    if (activeVoyage) return null;
    return voyages
      .filter(v => v.status === 'completed')
      .sort((a, b) => new Date(b.completedAt || 0).getTime() - new Date(a.completedAt || 0).getTime())[0];
  }, [voyages, activeVoyage]);

  return (
    <div className="bg-white rounded-lg ring-1 ring-gray-100 shadow-md p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Vessel Status
        </h3>
        {statusConfig.animate && (
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 ${statusConfig.dotColor} rounded-full animate-pulse`} />
            <span className="text-xs text-green-600 font-medium">Live</span>
          </div>
        )}
      </div>

      <div>
        {/* Status Row */}
        <div className="flex items-center justify-between mb-4">
          {/* Status Badge */}
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${statusConfig.bgColor} ${statusConfig.textColor} border ${statusConfig.borderColor}`}>
            <StatusIcon className="h-4 w-4" />
            <span className="text-sm font-medium">{statusConfig.label}</span>
          </div>

          {/* POB */}
          <div className="flex items-center gap-2 text-slate-600">
            <Users className="h-4 w-4" />
            <span className="text-sm font-medium">{totalPOB} POB</span>
          </div>
        </div>

        {/* Fuel Gauge */}
        {fuelLevel !== null && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
              <span className="flex items-center gap-1">
                <Fuel className="h-3.5 w-3.5" />
                Fuel Level
              </span>
              <span className={`font-medium ${fuelColor?.text}`}>{fuelLevel}%</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                style={{ width: `${fuelLevel}%` }}
                className={`h-full ${fuelColor?.bg} rounded-full transition-all duration-500`}
              />
            </div>
          </div>
        )}

        {/* Active Voyage Info */}
        {activeVoyage && (
          <div className="border-t border-slate-200 pt-4 mb-4">
            {/* Voyage Number */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Active Voyage
              </span>
              <span className="text-sm font-semibold text-slate-700">
                {activeVoyage.voyageNumber}
              </span>
            </div>

            {/* Route - cleaner horizontal layout */}
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 text-center">
                <p className="text-xs text-slate-500 mb-0.5">From</p>
                <p className="text-sm font-medium text-slate-800 truncate">
                  {portLabel(activeVoyage.from, '---')}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <div className="flex-1 text-center">
                <p className="text-xs text-slate-500 mb-0.5">To</p>
                <p className="text-sm font-medium text-slate-800 truncate">
                  {portLabel(activeVoyage.to, '---')}
                </p>
              </div>
            </div>

            {/* DEP & ETA times */}
            <div className="flex items-center justify-between text-sm">
              {activeVoyage.departureTime && (
                <div className="flex items-center gap-1.5 text-slate-600">
                  <Clock className="h-4 w-4" />
                  <span className="font-medium">
                    DEP {formatDateTime(activeVoyage.departureTime)}
                  </span>
                </div>
              )}
              {activeVoyage.eta && (
                <div className="text-slate-500">
                  ETA <span className="font-medium text-slate-700">{formatDateTime(activeVoyage.eta)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* No Active Voyage - Show current location */}
        {!activeVoyage && (
          <div className="border-t border-slate-200 pt-4 mb-4">
            {lastVoyage?.to?.name ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="h-4 w-4 text-slate-400" />
                  <span className="text-sm font-medium text-slate-700">
                    {lastVoyage.to.name}
                  </span>
                </div>
                {lastVoyage.completedAt && (
                  <p className="text-xs text-slate-400 ml-6">
                    Arrived {formatDistanceToNow(lastVoyage.completedAt)}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500 text-center">
                No voyage history
              </p>
            )}
          </div>
        )}

        {/* Quick Action */}
        <button
          onClick={() => onNavigate('operations')}
          className="w-full py-2 px-3 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {activeVoyage ? (
            <>
              <Ship className="h-4 w-4" />
              View Voyage
            </>
          ) : (
            <>
              <Navigation className="h-4 w-4" />
              Start New Voyage
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default VesselStatusCard;
